import { Component, OnInit } from '@angular/core';
import { Device } from '@awesome-cordova-plugins/device/ngx';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorModalComponent } from './components/modals/http-error-modal/http-error-modal.component';
import { Broadcaster } from './services/events/broadcaster.class';
import { PlatformService } from './services/platform/platform.service';
import { Logger, LoggingService } from "ionic-logging-service";
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx';
import { NFC } from '@awesome-cordova-plugins/nfc/ngx';
import { Deeplinks } from '@awesome-cordova-plugins/deeplinks/ngx';
import { AuthService } from './services/auth/auth.service';
import { ShareExtensionService } from './services/share-extension/share-extension.service';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {

  public content: any;
  private errorModal: any = null;
  private isCordova: boolean = this.platformService.isCordova();
  public isIOS: boolean = this.platformService.isIOS();
  public isAndroid: boolean = this.platformService.isAndroid();
  private logger: Logger;
  public nav: any;
  private statusBarColor: string = '#000000';
  public showIpadSpinner = false;

  constructor(
    private authService: AuthService,
    private broadcaster: Broadcaster,
    private translateService: TranslateService,
    private device: Device,
    private modalCtrl: ModalController,
    private nfc: NFC,
    private platform: Platform,
    private platformService: PlatformService,
    private screenOrientation: ScreenOrientation,
    private deeplinks: Deeplinks,
    private loggingService: LoggingService,
    private shareExtensionService: ShareExtensionService,
    private statusBar: StatusBar
  ) {
    // Initialize logger and share handler immediately so cold-start URLs
    // delivered by the handleopenurl plugin right after deviceready are caught.
    this.logger = this.loggingService.getLogger("[AppComponent]");
    if (this.isCordova && this.isIOS) {
      this.setupShareExtensionHandler();
    }

    this.platform.ready().then(() => {
      const methodName = "ctor";
      this.logger.entry(methodName);
      this.logger.info(methodName + 'platform isCordova: ' + this.isCordova);
      this.setupApp();
      this.logger.exit(methodName);
    });
  }

  ngOnInit(): void {
    this.translateService.setDefaultLang('en');
    this.broadcaster.on('ipad_spinner_show').subscribe((securePrintLevel: string) => {
      this.showIpadSpinner = true;
    });
    this.broadcaster.on('ipad_spinner_hide').subscribe((securePrintLevel: string) => {
      this.showIpadSpinner = false;
    });
    this.authService.verifyUserCredentials().subscribe();
  }

  private setupStatusBar(): void {
    this.statusBar.styleLightContent();
    this.statusBar.hide();
    this.statusBar.show();
    if (this.isIOS) {
      this.statusBar.overlaysWebView(false);
      this.statusBar.backgroundColorByHexString(this.statusBarColor);
    }
  }

  private lockScreenOrientation() {
    this.screenOrientation.lock('portrait');
  }

  private checkCurrentDevice() {
    const methodName = 'checkCurrentDevice() ';
    this.logger.info(methodName + 'Device model: ' + this.device.model);
    this.logger.info(methodName + 'Device platform: ' + this.device.platform);
    this.logger.info(methodName + 'Device version: ' + this.device.version);
  }

  public setupErrorRedirect(): void {
    this.logger.info('setupErrorRedirect()');
    this.broadcaster.on('showErrorModal').subscribe(errorObject => {
      this.showHttpErrorModal(errorObject);
    });
  }

  private async showHttpErrorModal(errorObject: any): Promise<void> {
    this.logger.info('showHttpErrorModal()');
    if (this.errorModal === null) {
      const modalType: string = 'http-error-modal';
      this.errorModal = await this.modalCtrl.create({
        component: HttpErrorModalComponent,
        componentProps: {errorObject},
        cssClass: modalType,
        backdropDismiss: false
      });

      await this.errorModal.present();
    }

    this.errorModal.onDidDismiss(modalData => {
      this.errorModal = null;
    });
  }

  private setPreventNFCListener() {
    const nfcReaderFlags = this.nfc.FLAG_READER_NFC_A | this.nfc.FLAG_READER_NFC_V;

    this.nfc.readerMode(nfcReaderFlags).subscribe(
      (tag) => {
          console.log(tag);
      },
      (err) => {
          console.log('Error reading tag', err);
      }
    );
  }

  private setupApp(): void {
    if (this.isCordova) {
      this.setupStatusBar();
      this.lockScreenOrientation();
      this.checkCurrentDevice();
      if (this.isAndroid) {
        this.setPreventNFCListener();
      }
      if (this.isIOS) {
        this.setupShareExtensionDeeplinkListener();
      }
    }
    this.setupErrorRedirect();
  }

  // Intercepts printixapp://share?files=... URLs before the OAuth deeplink handler.
  // Fires for both warm-app and cold-start (Cordova delivers queued URLs after
  // platform.ready(), which has already resolved by the time setupApp() runs).
  private setupShareExtensionHandler(): void {
    const methodName = 'setupShareExtensionHandler';
    this.logger.info(methodName);

    const existing = (window as any).handleOpenURL as ((url: string) => void) | undefined;
    (window as any).handleOpenURL = (url: string) => {
      if (this.isShareExtensionUrl(url)) {
        this.logger.info('ShareExtension: handleOpenURL detected share URL');
        this.shareExtensionService.handleShareURL(url);
        return;
      }
      if (existing) {
        existing.call(window, url);
      }
    };
  }

  // also listen through the deeplinks plugin channel,
  // because some iOS launches are delivered there instead of handleOpenURL.
  private setupShareExtensionDeeplinkListener(): void {
    const methodName = 'setupShareExtensionDeeplinkListener';
    this.logger.info(methodName);

    this.deeplinks.route({
      '/share': AppComponent,
      '/file': AppComponent
    }).subscribe((match) => {
      const url = match?.$link?.url ?? '';
      if (this.isShareExtensionUrl(url)) {
        this.logger.info('ShareExtension: deeplinks MATCH detected share URL');
        this.shareExtensionService.handleShareURL(url);
      }
    }, (noMatch) => {
      const url = noMatch?.$link?.url ?? '';
      if (this.isShareExtensionUrl(url)) {
        this.logger.info('ShareExtension: deeplinks NOMATCH detected share URL');
        this.shareExtensionService.handleShareURL(url);
      }
    });
  }

  private isShareExtensionUrl(url: string): boolean {
    return url.startsWith('printixapp://share') || url.includes('://file://');
  }
}